import React from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@shared/auth/AuthContext';
import { InstallPrompt } from '@shared/components/InstallPrompt';
import { AppInitializer } from './components/AppInitializer';
import { AppLayout } from './components/Layout';
import { ModalRenderer } from './components/ModalRenderer';
import { TransactionEditProvider } from './components/Transactions/TransactionEditProvider';
import { PageContextProvider } from './contexts/PageContext';
import AppRoutes from './routes';

export function App() {
  const { isAuthenticated, isLoading: authLoading, sessionUserId } = useAuth();
  const location = useLocation();

  // Check if we're on a public page (no layout/nav needed)
  const isPublicPage = ['/', '/login', '/callback'].includes(location.pathname);

  // Render public pages without layout
  if (isPublicPage) {
    return <AppRoutes />;
  }

  // Show loading while checking auth (prevents nav from making API calls before auth is ready)
  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-screen text-lg text-text-muted">
        Loading...
      </div>
    );
  }

  // Redirect to login if not authenticated (for non-public pages)
  if (!isAuthenticated) {
    return <AppRoutes />;
  }

  // Render app pages with layout. Keyed by the session's user so a session
  // swap remounts the whole signed-in tree — no component state (chat
  // history, form drafts, selections) carries over to another user (WEBD-03).
  return (
    <PageContextProvider key={sessionUserId ?? 'signed-in'}>
      <TransactionEditProvider>
        <AppLayout>
          {/* Dev Environment Indicator */}
          {import.meta.env.DEV && (
            <div className="bg-amber-600 text-white text-center text-sm py-1.5 px-4 font-medium">
              🔧 DEVELOPMENT ENVIRONMENT - Using dev schema
            </div>
          )}

          {/* App initialization (cleanup expired options, etc.) */}
          <AppInitializer />

          {/* Main Application Content */}
          <AppRoutes />

          {/* PWA Install Prompt (mobile only) */}
          <InstallPrompt />
        </AppLayout>
      </TransactionEditProvider>

      {/* App-level modals (confirm/alert) */}
      <ModalRenderer />
    </PageContextProvider>
  );
}

export default App;
